import React, { FC, useEffect, useRef } from 'react'
import { styled } from '@linaria/react'
import { Typography } from '../ui/Typography'
import { CollapsableBox } from '../ui/CollapsableBox'
import { Markdown } from '../ui/Markdown'
import { colors } from '../styles/colors'
import { FormattedMessage } from 'react-intl'
import { SocialLinks } from '../components/SocialLinks'
import { useFaq } from '../hooks/useFaq'
import { useAppContext } from '../contexts/App.context'
import { useSearchParams } from 'react-router'

export const FaqPage: FC = () => {
  const { data: faqItems, isLoading, isError } = useFaq()
  const { language } = useAppContext()
  const [searchParams] = useSearchParams()
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const detailsRefs = useRef<Record<string, HTMLDetailsElement | null>>({})

  // Handle deep linking to specific FAQ section
  useEffect(() => {
    const sectionId = searchParams.get('section')

    if (sectionId && sectionRefs.current[sectionId]) {
      // Expand the targeted section
      const detailsElement = detailsRefs.current[sectionId]
      if (detailsElement) {
        detailsElement.open = true
      }

      // Wait for layout to settle, then scroll
      setTimeout(() => {
        sectionRefs.current[sectionId]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100)
    }
  }, [searchParams, faqItems])

  return (
    <Container>
      <Header>
        <Typography fontSize={24} fontWeight={600} color={colors.textPrimary}>
          <FormattedMessage id="faq.title" defaultMessage="База знаний" />
        </Typography>
      </Header>

      <Content>
        {isLoading && (
          <Typography fontSize={14} color={colors.textSecondary}>
            <FormattedMessage
              id="status-loading"
              defaultMessage="Загружается"
            />
          </Typography>
        )}

        {isError && (
          <Typography fontSize={14} color={colors.textSecondary}>
            <FormattedMessage
              id="error-loading"
              defaultMessage="Ошибка загрузки. Повторить"
            />
          </Typography>
        )}

        {faqItems?.map(item => {
          const currentLanguage =
            language === 'ru' || language === 'en' ? language : 'ru'
          const title = item.title[currentLanguage] || item.title.ru
          const content = item.content[currentLanguage] || item.content.ru

          return (
            <div
              key={item.id}
              ref={el => {
                sectionRefs.current[item.id] = el
              }}
            >
              <CollapsableBox
                ref={el => {
                  detailsRefs.current[item.id] = el
                }}
                title={title}
                defaultExpanded={item.defaultExpanded}
              >
                <Markdown>{content}</Markdown>
              </CollapsableBox>
            </div>
          )
        })}
      </Content>

      <Footer>
        <Typography fontSize={14} fontWeight={400} color={colors.textSecondary}>
          <FormattedMessage
            id="faq.footer"
            defaultMessage="Остались вопросы, спросите в сообществе"
          />
        </Typography>

        <StyledSocialLinks />
      </Footer>
    </Container>
  )
}

export const faqPagePath = '/faq'

const Container = styled.div`
  margin: 0 auto;
  width: 100%;
`

const Header = styled.div`
  margin-bottom: 24px;
  text-align: center;
`

const Content = styled.div`
  margin-bottom: 32px;
`

const Footer = styled.div`
  text-align: start;
  margin-bottom: 20px;
`

const StyledSocialLinks = styled(SocialLinks)`
  margin-top: 12px;
  max-width: 200px;
`
